import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bookmark, BookOpen, Trash2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const BookmarksPage = () => {
  const [bookmarks, setBookmarks] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem("bible-bookmarks");
    if (saved) {
      const parsed = JSON.parse(saved);
      // Sort by date, most recent first
      parsed.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setBookmarks(parsed);
    }
  }, []);

  const removeBookmark = (id) => {
    const newBookmarks = bookmarks.filter(b => b.id !== id);
    setBookmarks(newBookmarks);
    localStorage.setItem("bible-bookmarks", JSON.stringify(newBookmarks));
    toast("Signet retiré");
  };

  const clearAllBookmarks = () => {
    setBookmarks([]);
    localStorage.setItem("bible-bookmarks", JSON.stringify([]));
    toast("Tous les signets ont été supprimés");
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-paper animate-fade-in">
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        {/* Header */}
        <header className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Bookmark className="h-6 w-6 text-rubric-red" strokeWidth={1.5} />
            <h1 className="font-heading text-3xl md:text-4xl text-ink-primary">
              Mes signets
            </h1>
          </div>
          <p className="font-body text-ink-secondary">
            {bookmarks.length} {bookmarks.length === 1 ? 'passage sauvegardé' : 'passages sauvegardés'}
          </p>
          <div className="divider-ornate mt-8">
            <span className="font-decorative text-gold-leaf px-4">✦</span>
          </div>
        </header>

        {bookmarks.length === 0 ? (
          <div className="text-center py-16">
            <Bookmark className="h-16 w-16 text-border mx-auto mb-6" strokeWidth={1} />
            <h2 className="font-heading text-2xl text-ink-primary mb-3">
              Aucun signet
            </h2>
            <p className="font-body text-ink-secondary mb-8 max-w-md mx-auto">
              Marquez vos passages préférés en cliquant sur l'icône signet lors de votre lecture.
            </p>
            <Button asChild className="bg-ink-primary hover:bg-ink-secondary text-paper">
              <Link to="/bible/genese/1">
                <BookOpen className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Commencer la lecture
              </Link>
            </Button>
          </div>
        ) : (
          <>
            {/* Bookmarks list */}
            <div className="space-y-4">
              {bookmarks.map((bookmark) => (
                <Card 
                  key={bookmark.id} 
                  className="bg-paper-dark border-border group hover:border-rubric-red transition-all duration-300"
                  data-testid={`bookmark-${bookmark.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <Link
                        to={`/bible/${bookmark.bookId}/${bookmark.chapter}`}
                        className="flex-1 group/link"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-heading text-lg text-ink-primary group-hover/link:text-rubric-red transition-colors">
                            {bookmark.bookName}
                          </h3>
                          <span className="font-decorative text-sm text-rubric-red">
                            {bookmark.chapter}:{bookmark.verse}
                          </span>
                          <ArrowRight className="h-4 w-4 text-ink-secondary opacity-0 group-hover/link:opacity-100 group-hover/link:translate-x-1 transition-all" strokeWidth={1.5} />
                        </div>
                        <p className="font-body text-ink-secondary text-sm leading-relaxed">
                          {bookmark.text}
                        </p>
                        <p className="font-body text-xs text-ink-secondary mt-3 italic">
                          Ajouté le {formatDate(bookmark.createdAt)}
                        </p>
                      </Link>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeBookmark(bookmark.id)}
                        className="text-ink-secondary hover:text-rubric-red opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-bookmark-${bookmark.id}`}
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Clear all */}
            <div className="mt-12 text-center">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="text-ink-secondary hover:text-rubric-red border-border hover:border-rubric-red"
                    data-testid="clear-all-bookmarks"
                  >
                    <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Supprimer tous les signets
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-paper border-border">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-heading text-ink-primary">
                      Supprimer tous les signets ?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="font-body text-ink-secondary">
                      Cette action est irréversible. Tous vos {bookmarks.length} signets seront définitivement supprimés.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="font-body">Annuler</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={clearAllBookmarks}
                      className="bg-rubric-red hover:bg-red-700 text-paper font-body"
                    >
                      Supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BookmarksPage;
